import { z } from 'zod';
import type { Tier } from './tier';
import { UPSCALE_MODES, type UpscaleMode } from './upscale';

/**
 * `content/tuning/render.json` — the resolution policy's WHOLE constant table.
 *
 * Mirrors `src/sim/tuning.ts` and `src/exploration/axon/brain/tuning.ts` exactly: a zod schema
 * plus one `parse` entry point, and nothing else. The JSON is imported by the impure module that
 * owns the renderer (`src/presentation/scene.ts`) and handed here — a static JSON import is
 * inlined into whatever chunk imports it, and this one belongs in the eager core because the
 * renderer is the eager core.
 *
 * PURE. No three, no DOM, no clock, no randomness — zod and arithmetic only. `$`-prefixed
 * sibling keys in the JSON carry the rationale for each number and are deliberately ignored
 * (zod strips unknown keys); read them before re-tuning anything.
 */

const pos = z.number().positive();
const frac = z.number().min(0).max(1);

export const ResolutionTuningSchema = z.object({
  /**
   * WHICH UPSCALER EACH TIER GETS (plan stages D/E). A per-tier DEFAULT, not a hard-coded
   * choice, because the answer is genuinely different per backend: every post pass runs at
   * OUTPUT resolution, and the WebGL2 fallback costs ~2x the WebGPU backend per pixel before a
   * single post pass exists. Overridable at runtime with `?upscale=`, in the same spirit as
   * `?tier=` and `?dpr=`.
   */
  upscale: z.object({
    tier0: z.enum(UPSCALE_MODES),
    tier1: z.enum(UPSCALE_MODES),
    tier2: z.enum(UPSCALE_MODES),
  }),
  output: z.object({
    pixelRatioCap: z.object({ tier0: pos, tier1: pos, tier2: pos }),
    minPixelRatio: pos,
  }),
  controller: z.object({
    enabled: z.boolean(),
    /**
     * Descending, first rung 1.0. Both properties are load-bearing: `snapDown` walks it in order
     * and index 0 is the "native" rung every reset returns to, so an unsorted or 0.9-leading
     * ladder would silently make the controller unable to reach full resolution.
     */
    ladder: z.array(z.number().positive().max(1)).min(2),
    warmupFrames: z.number().int().nonnegative(),
    resumeFrames: z.number().int().nonnegative(),
    sampleWindow: z.number().int().min(2),
    cooldownFrames: z.number().int().nonnegative(),
    cpuReserveMs: z.number().nonnegative(),
    headroomFrac: frac,
    maxRefreshDivisor: z.number().int().min(1),
    minBudgetMs: pos,
    defaultDisplayHz: pos,
    displayPeriodSamples: z.number().int().min(2),
    raiseThresholdFrac: frac,
    raiseWindows: z.number().int().min(1),
    panicFactor: z.number().min(1),
    panicFrames: z.number().int().min(1),
    missFactor: z.number().min(1),
    missFraction: frac,
    wallPanicFactor: z.number().min(1),
    probeSeconds: pos,
    probeMaxSeconds: pos,
    probeVerifySeconds: pos,
  }),
});

export type ResolutionTuning = z.infer<typeof ResolutionTuningSchema>;
export type ResolutionControllerTuning = ResolutionTuning['controller'];

/**
 * Parse + the two structural invariants a zod type cannot express. Both are throw-worthy: a
 * malformed ladder does not degrade, it makes the controller quietly wrong in a direction nobody
 * would notice until a device report came back saying "it never looks sharp".
 */
export function parseResolutionTuning(raw: unknown): ResolutionTuning {
  const parsed = ResolutionTuningSchema.parse(raw);
  const { ladder } = parsed.controller;
  if (ladder[0] !== 1) {
    throw new Error(`render.json: controller.ladder must start at 1.0 (got ${String(ladder[0])})`);
  }
  for (let i = 1; i < ladder.length; i++) {
    if (!(ladder[i]! < ladder[i - 1]!)) {
      throw new Error(`render.json: controller.ladder must be strictly descending (index ${i})`);
    }
  }
  return parsed;
}

/**
 * The upscale mode for a tier, with the `?upscale=` override applied. Pure, and the only place
 * the tier→mode table is read — `pipeline.ts` is handed a mode and never asks where it came from.
 */
export function resolveUpscaleMode(
  tier: Tier,
  tuning: ResolutionTuning,
  override?: UpscaleMode | null,
): UpscaleMode {
  if (override != null) return override;
  return tuning.upscale[`tier${tier}` as 'tier0' | 'tier1' | 'tier2'];
}

/**
 * OUTPUT resolution — what the compositor presents, and what every future post pass will cost.
 * A settings/tier decision, re-evaluated on resize and on a DPI change; NEVER driven by the
 * frame-time controller. Pure so the DPI-change behaviour is testable without a browser.
 *
 * `override` is the `?dpr=` device-testing knob, in the same spirit as `?tier=` — it wins over
 * the tier cap but still clamps to a sane range, because a hand-typed `?dpr=8` on a 4K panel is
 * a browser-killing 130 Mpix drawing buffer.
 */
export function resolveOutputPixelRatio(
  devicePixelRatio: number,
  tier: Tier,
  tuning: ResolutionTuning,
  override?: string | null,
): number {
  const cap = tuning.output.pixelRatioCap[`tier${tier}` as 'tier0' | 'tier1' | 'tier2'];
  const dpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  const forced = override != null && override !== '' ? Number(override) : NaN;
  const wanted = Number.isFinite(forced) && forced > 0 ? forced : Math.min(dpr, cap);
  return Math.min(Math.max(wanted, tuning.output.minPixelRatio), 4);
}
